/* Theme ArtWeb v1.0.0 · © Studio Art Web · https://studioartweb.com.br · não editar sem falar com o Studio */
/*
 * Harmony Health | hh-kit.js
 * Vender mais por pedido, sem biblioteca:
 * - <hh-kit-comprar>: botão "Adicionar o kit" (e "Adicionar" da sacola). Lê os
 *   items[][id] do formulário, adiciona tudo de uma vez por /cart/add.js e avisa o
 *   Horizon (CartAddEvent): a sacola lateral abre e a lista da sacola se atualiza.
 *   Sem sacola lateral (ou na página /cart), segue o fluxo da página.
 * - Faixa "Monte sua rotina" (<hh-kit-comprar data-hh-kit-faixa>): cada complemento
 *   tem uma caixa de marcar; o total, o Pix e a linha de frete grátis se refazem na hora.
 *   Na página de produto, acompanha a variante escolhida no formulário principal.
 * - <hh-kit-lista>: quando não há kit configurado, busca o kit padrão nas
 *   recomendações da Shopify (complementares; se vazio, relacionadas). Também serve de
 *   lugar vago para a faixa dentro da seção de compra (hh-pdp-principal).
 * - <hh-kit-sacola>: refaz a seção "Complete sua rotina" quando a sacola muda.
 * - <hh-avise-me>: em produto esgotado, coloca o formulário "Avise-me" no lugar do
 *   botão de compra. Com ?hh_avise=teste simula o esgotado só naquela visita.
 * Sem JavaScript, os formulários continuam funcionando (envio normal).
 */
import { CartAddEvent } from '@theme/events';

const rotas = () => (window.Theme && window.Theme.routes) || {};
const raiz = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/**
 * Adiciona vários itens à sacola e avisa o tema.
 * @param {{id:number, quantity:number}[]} itens
 * @param {string} origem
 */
async function adicionaNaSacola(itens, origem) {
  const secoes = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
  const rota = rotas().cart_add_url || `${raiz()}cart/add.js`;
  const resp = await fetch(rota, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: itens, sections: secoes.join(',') }),
  });
  const json = await resp.json();
  if (!resp.ok || json.status) throw new Error(json.description || json.message || '');
  let carrinho;
  try {
    carrinho = await (await fetch(`${raiz()}cart.js`)).json();
  } catch (_) {
    carrinho = undefined;
  }
  document.dispatchEvent(
    new CartAddEvent(carrinho, origem, {
      source: 'hh-kit',
      itemCount: itens.reduce((s, i) => s + i.quantity, 0),
      productId: '',
      variantId: String(itens[0].id),
      sections: json.sections,
    })
  );
  return carrinho;
}

class HHKitComprar extends HTMLElement {
  #ctrl = new AbortController();

  connectedCallback() {
    this.form = this.querySelector('[data-hh-kit-form]') || (this.matches('form') ? this : null);
    this.form?.addEventListener('submit', (e) => this.envia(e), { signal: this.#ctrl.signal });
    if (this.hasAttribute('data-hh-kit-faixa')) this.#ligaFaixa();
  }

  #ligaFaixa() {
    const signal = this.#ctrl.signal;
    this.form?.addEventListener('change', () => this.recalcula(), { signal });
    // Página de produto: o item "este produto" segue a variante escolhida na compra.
    const compra = this.closest('hh-pdp-compra');
    const principal = compra?.querySelector('[data-hh-form]');
    if (compra && principal) {
      let variantes = [];
      try {
        variantes = JSON.parse(compra.querySelector('[data-hh-dados]')?.textContent || '{}').variantes || [];
      } catch (_) {}
      principal.addEventListener(
        'change',
        () => {
          const marcada = principal.querySelector('[data-hh-variante]:checked');
          const v = marcada && variantes.find((x) => String(x.id) === marcada.value);
          const alvo = this.querySelector('[data-hh-rotina-atual]');
          if (!v || !alvo || !v.disponivel) return;
          alvo.value = String(v.id);
          alvo.dataset.preco = String(v.preco);
          const preco = this.querySelector('.hh-rotina__item--atual .hh-rotina__preco');
          if (preco) preco.textContent = this.#dinheiro(v.preco);
          this.recalcula();
        },
        { signal }
      );
    }
    this.recalcula();
  }

  #dinheiro(centavos) {
    try {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: this.dataset.moeda || 'BRL' }).format(centavos / 100);
    } catch (_) {
      return `R$ ${(centavos / 100).toFixed(2).replace('.', ',')}`;
    }
  }

  recalcula() {
    const itens = [...this.querySelectorAll('[data-hh-kit-variante]')];
    const ativos = itens.filter((el) => !(el instanceof HTMLInputElement && el.type === 'checkbox' && !el.checked));
    itens.forEach((el) => el.closest('.hh-rotina__item')?.toggleAttribute('data-fora', !ativos.includes(el)));
    const total = ativos.reduce((s, el) => s + (Number(el.dataset.preco) || 0), 0);
    const pct = ativos.length > 1 ? Number(this.dataset.pct) || 0 : 0;
    const final = pct > 0 ? Math.round((total * (100 - pct)) / 100) : total;
    const pix = Number(this.dataset.pix) || 0;
    const valorPix = pix > 0 ? Math.round((final * (100 - pix)) / 100) : final;
    const frete = Number(this.dataset.frete) || 0;
    const poe = (sel, txt) => {
      const el = this.querySelector(sel);
      if (el) el.textContent = txt;
    };
    poe('[data-hh-rotina-total]', this.#dinheiro(final));
    poe('[data-hh-rotina-pix]', this.#dinheiro(valorPix));
    poe('[data-hh-rotina-n]', ativos.length === 1 ? '(1 item)' : `(${ativos.length} itens)`);
    const de = this.querySelector('[data-hh-rotina-de]');
    if (de) {
      de.hidden = pct === 0;
      de.lastChild && (de.lastChild.textContent = ` ${this.#dinheiro(total)}`);
    }
    this.querySelector('[data-hh-rotina-desconto]')?.toggleAttribute('hidden', pct === 0);
    this.querySelector('[data-hh-rotina-frete]')?.toggleAttribute('hidden', !(frete > 0 && valorPix >= frete));
    const botao = this.querySelector('[data-hh-kit-botao]');
    if (botao) {
      const so = ativos.length < 2;
      botao.disabled = so;
      botao.toggleAttribute('aria-disabled', so);
      if (so) botao.setAttribute('aria-disabled', 'true');
    }
  }

  disconnectedCallback() {
    this.#ctrl.abort();
  }

  async envia(e) {
    e.preventDefault();
    const botao = this.querySelector('[data-hh-kit-botao]');
    const rot = botao?.querySelector('[data-hh-rotulo]');
    const erro = this.querySelector('[data-hh-kit-erro]');
    if (!botao || botao.getAttribute('aria-busy') === 'true') return;
    const itens = [...this.querySelectorAll('[data-hh-kit-variante]')]
      .filter((el) => !(el instanceof HTMLInputElement && el.type === 'checkbox' && !el.checked))
      .map((el) => ({ id: Number(el.value), quantity: 1 }))
      .filter((i) => i.id > 0);
    if (!itens.length) return;
    const texto = rot?.textContent || '';
    botao.setAttribute('aria-busy', 'true');
    if (rot) rot.textContent = 'Adicionando...';
    if (erro) erro.hidden = true;
    try {
      await adicionaNaSacola(itens, this.id || 'hh-kit');
      if (rot) rot.textContent = 'Adicionado';
      botao.setAttribute('data-hh-estado', 'adicionado');
      const naSacola = !!document.querySelector('hh-kit-sacola');
      if (!document.querySelector('cart-drawer-component') && !naSacola) {
        window.location.href = rotas().cart_url || `${raiz()}cart`;
        return;
      }
      setTimeout(() => {
        botao.removeAttribute('data-hh-estado');
        if (rot && this.isConnected) rot.textContent = texto;
      }, 2400);
    } catch (err) {
      if (erro) {
        erro.textContent = err && err.message ? err.message : 'Não foi possível adicionar. Tente de novo.';
        erro.hidden = false;
      }
      if (rot) rot.textContent = texto;
    } finally {
      botao.removeAttribute('aria-busy');
    }
  }
}

class HHKitLista extends HTMLElement {
  connectedCallback() {
    const url = this.dataset.url;
    if (!url || this.dataset.carregado) return;
    const sec = this.closest('.hh-kit-sec') || this.parentElement || this;
    const obs = new IntersectionObserver(
      (en) => {
        if (en[0].isIntersecting) {
          obs.disconnect();
          this.busca(url);
        }
      },
      { rootMargin: '1200px 0px' }
    );
    obs.observe(sec);
  }

  // A faixa vem com os textos padrão da seção "leve junto"; o lugar vago pode trocar.
  #textos(faixa) {
    const { titulo, botao, texto } = this.dataset;
    if (titulo) {
      const t = faixa.querySelector('[data-hh-rotina-titulo]');
      if (t) t.textContent = titulo;
    }
    if (botao) {
      const b = faixa.querySelector('[data-hh-kit-botao] [data-hh-rotulo]');
      if (b) b.textContent = botao;
    }
    if (texto !== undefined) {
      const p = faixa.querySelector('.hh-rotina__texto');
      if (p && texto) p.textContent = texto;
      else if (p) p.remove();
    }
  }

  async busca(url) {
    this.dataset.carregado = '1';
    for (const intencao of ['complementary', 'related']) {
      try {
        const html = await (await fetch(`${url}&intent=${intencao}`)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const faixa = doc.querySelector('[data-hh-kit-faixa]');
        if (faixa) {
          const nova = /** @type {HTMLElement} */ (document.importNode(faixa, true));
          this.#textos(nova);
          const slot = this.querySelector('[data-hh-kit-slot]');
          if (slot) slot.replaceChildren(nova);
          else this.replaceChildren(nova);
          this.closest('.hh-kit-sec')?.removeAttribute('data-vazia');
          this.removeAttribute('data-vaga');
          return;
        }
        const cartoes = doc.querySelectorAll('[data-hh-kit-grade] > [data-hh-kit-card]');
        const grade = this.querySelector('[data-hh-kit-grade]');
        if (cartoes.length && grade) {
          grade.replaceChildren(...[...cartoes].map((c) => document.importNode(c, true)));
          this.closest('.hh-kit-sec')?.removeAttribute('data-vazia');
          return;
        }
      } catch (_) {
        /* sem recomendação: a seção continua vazia */
      }
    }
  }
}

class HHKitSacola extends HTMLElement {
  #ctrl = new AbortController();
  #pedido = null;

  connectedCallback() {
    document.addEventListener('cart:update', () => this.atualiza(), { signal: this.#ctrl.signal });
    this.querySelectorAll('[data-hh-kit-form]').forEach((f) => this.#liga(f));
  }

  disconnectedCallback() {
    this.#ctrl.abort();
    this.#pedido?.abort();
  }

  #liga(form) {
    if (form.dataset.hhLigado) return;
    form.dataset.hhLigado = '1';
    form.addEventListener(
      'submit',
      async (e) => {
        e.preventDefault();
        const botao = form.querySelector('[data-hh-kit-botao]');
        const rot = botao?.querySelector('[data-hh-rotulo]');
        const erro = form.querySelector('[data-hh-kit-erro]');
        const id = Number(form.querySelector('[data-hh-kit-variante]')?.value);
        if (!botao || !id || botao.getAttribute('aria-busy') === 'true') return;
        const texto = rot?.textContent || '';
        botao.setAttribute('aria-busy', 'true');
        if (rot) rot.textContent = 'Adicionando...';
        if (erro) erro.hidden = true;
        try {
          await adicionaNaSacola([{ id, quantity: 1 }], 'hh-kit-sacola');
        } catch (err) {
          if (erro) {
            erro.textContent = err && err.message ? err.message : 'Não foi possível adicionar. Tente de novo.';
            erro.hidden = false;
          }
          if (rot) rot.textContent = texto;
          botao.removeAttribute('aria-busy');
        }
      },
      { signal: this.#ctrl.signal }
    );
  }

  async atualiza() {
    const secao = this.dataset.secao;
    if (!secao) return;
    this.#pedido?.abort();
    this.#pedido = new AbortController();
    try {
      const url = `${rotas().cart_url || `${raiz()}cart`}?section_id=${encodeURIComponent(secao)}`;
      const html = await (await fetch(url, { signal: this.#pedido.signal })).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const novo = doc.querySelector('hh-kit-sacola');
      if (!novo) return;
      const foco = this.contains(document.activeElement);
      this.innerHTML = novo.innerHTML;
      this.querySelectorAll('[data-hh-kit-form]').forEach((f) => this.#liga(f));
      if (foco) {
        const alvo = this.querySelector('h2') || document.querySelector('cart-items-component');
        if (alvo instanceof HTMLElement) {
          alvo.setAttribute('tabindex', '-1');
          alvo.focus({ preventScroll: true });
        }
      }
    } catch (_) {
      /* pedido cancelado ou sem rede: mantém o que está na tela */
    }
  }
}

class HHAviseMe extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ligado) return;
    let teste = false;
    try {
      teste = new URLSearchParams(window.location.search).get('hh_avise') === 'teste';
    } catch (_) {}
    const esgotado = this.dataset.esgotado === 'true';
    if (!esgotado && !teste) return;
    this.dataset.ligado = '1';

    let conteudo = this.querySelector('[data-hh-avise-conteudo]');
    const tpl = this.querySelector('template[data-hh-avise-teste]');
    if (!conteudo && tpl instanceof HTMLTemplateElement) {
      conteudo = document.createElement('div');
      conteudo.className = 'hh-pagina hh-avise-sec__fallback';
      conteudo.setAttribute('data-hh-avise-conteudo', '');
      conteudo.append(tpl.content.cloneNode(true));
      this.append(conteudo);
      this.hidden = false;
    }
    if (!conteudo) return;
    const caixa = conteudo.querySelector('.hh-avise');
    if (!caixa) return;

    // Remove cópias antigas (o editor de temas rerenderiza a seção).
    document.querySelectorAll(`.hh-avise[data-hh-avise-de="${this.closest('.shopify-section')?.id || ''}"]`).forEach((el) => el.remove());
    caixa.dataset.hhAviseDe = this.closest('.shopify-section')?.id || '';

    // Página de produto padrão: no lugar do botão "Esgotado" (ou do "Adicionar", no teste).
    const pdp = document.querySelector('hh-pdp-compra');
    if (pdp) {
      const botao = pdp.querySelector('button[disabled][aria-disabled="true"]') || (teste ? pdp.querySelector('[data-hh-adicionar]') : null);
      if (botao) {
        caixa.querySelector('[data-hh-avise-zap]')?.remove();
        botao.before(caixa);
        botao.hidden = true;
        if (teste) {
          pdp.querySelector('.hh-pagamentos')?.setAttribute('hidden', '');
          document.querySelector('[data-hh-barra]')?.setAttribute('hidden', '');
        }
        this.hidden = true;
        this.#mostraOk(caixa);
        return;
      }
    }

    // Landings: no lugar dos botões de compra do bloco do Horizon.
    const lp = document.querySelector('[data-hh-lp-compra-alvo]');
    if (lp) {
      const compra = lp.querySelector('product-form-component')?.closest('.shopify-block, buy-buttons-component, .buy-buttons-block') ||
        lp.querySelector('product-form-component');
      if (compra instanceof HTMLElement) {
        compra.before(caixa);
        compra.hidden = true;
        compra.style.display = 'none';
        lp.querySelector('.hh-sacola-info')?.setAttribute('hidden', '');
        if (teste) document.querySelector('hh-lp-barra')?.setAttribute('hidden', '');
        this.hidden = true;
        this.#outrosBotoes(caixa, teste);
        this.#mostraOk(caixa);
        return;
      }
    }
    // Sem área de compra conhecida: fica onde está, abaixo da compra.
    this.hidden = false;
    this.#mostraOk(caixa);
  }

  // Outros botões de compra da página (ex.: chamada final da landing) viram um atalho
  // para o formulário, em vez de um botão "Esgotado" desligado.
  #outrosBotoes(caixa, teste) {
    const texto = caixa.querySelector('.hh-avise__titulo')?.textContent.trim() || 'Avise-me quando voltar';
    document.querySelectorAll('product-form-component').forEach((pf) => {
      const bloco = pf.closest('.buy-buttons-block') || pf;
      if (!(bloco instanceof HTMLElement) || bloco.hidden || bloco.dataset.hhAviseLink) return;
      const botao = pf.querySelector('[ref="addToCartButton"]');
      if (!botao || (!botao.disabled && !teste)) return;
      bloco.dataset.hhAviseLink = '1';
      const a = document.createElement('a');
      a.className = 'hh-btn hh-btn--suave hh-btn--azul hh-btn--bloco hh-avise__link';
      a.href = `#${caixa.id}`;
      a.textContent = texto;
      a.addEventListener('click', () => {
        setTimeout(() => caixa.querySelector('input[type="email"]')?.focus({ preventScroll: true }), 50);
      });
      bloco.after(a);
      bloco.hidden = true;
      bloco.style.display = 'none';
    });
  }

  #mostraOk(caixa) {
    const ok = caixa.querySelector('[data-hh-avise-ok]');
    if (ok instanceof HTMLElement) {
      ok.scrollIntoView({ block: 'center' });
      ok.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('hh-kit-comprar')) customElements.define('hh-kit-comprar', HHKitComprar);
if (!customElements.get('hh-kit-lista')) customElements.define('hh-kit-lista', HHKitLista);
if (!customElements.get('hh-kit-sacola')) customElements.define('hh-kit-sacola', HHKitSacola);
if (!customElements.get('hh-avise-me')) customElements.define('hh-avise-me', HHAviseMe);
